import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../AppContext';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import LeaderboardSkeleton from '../components/Showdown/LeaderboardSkeleton';
import VibeOrb from '../components/VibeOrb';
import { Crown, Shield, User, Feather, Video, Award, Users, Loader2, ArrowUp, ArrowDown, SearchX, Flame, Network, Gem, Search, X, Scale, Share2 } from 'lucide-react';
import CompareStatsModal from '../components/CompareStatsModal';

const LeaderboardPage = () => {
    const { db, user } = useAppContext();
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [prevLeaderboardData, setPrevLeaderboardData] = useState([]);
    const [view, setView] = useState('reputation'); // Default to reputation
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timePeriod, setTimePeriod] = useState('all_time'); // 'daily', 'weekly', 'monthly', 'all_time'
    const [searchTerm, setSearchTerm] = useState('');
    const [compareMode, setCompareMode] = useState(false);
    const [selectedForComparison, setSelectedForComparison] = useState([]);
    const [message, setMessage] = useState('');

    const handleShareRank = async () => {
        const userEntry = leaderboardData.find(entry => entry.id === user?.uid);
        if (!userEntry) {
            setMessage("You are not currently ranked on this leaderboard.");
            return;
        }

        const userRank = leaderboardData.findIndex(entry => entry.id === user.uid) + 1;
        const shareText = `I'm Rank #${userRank} in ${categories.find(cat => cat.id === view)?.name} on Whispers of Harmony! Can you beat my score? #WhispersOfHarmony #Leaderboard`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'My Whispers of Harmony Rank',
                    text: shareText,
                    url: window.location.href,
                });
            } catch (error) {
                console.error('Error sharing rank:', error);
                setMessage("Failed to share rank.");
            }
        } else {
            navigator.clipboard.writeText(shareText + ' ' + window.location.href);
            setMessage("Rank copied to clipboard!");
        }
    };

    const getNextMilestone = (currentValue, viewType) => {
        const milestones = {
            reputation: [100, 500, 1000],
            earnings: [100, 500, 1000],
            whispers: [10, 50, 100],
        };
        const sequence = milestones[viewType] || [];
        return sequence.find(ms => currentValue < ms) || null;
    };

    const handleSelectForComparison = (entry) => {
        setSelectedForComparison(prev => {
            if (prev.some(s => s.id === entry.id)) {
                return prev.filter(s => s.id !== entry.id);
            }
            if (prev.length < 2) {
                return [...prev, entry];
            }
            return [prev[1], entry];
        });
    };

    const categories = useMemo(() => [
        { id: 'reputation', name: 'Reputation', icon: Shield },
        { id: 'earnings', name: 'Earnings', icon: Crown },
        { id: 'whispers', name: 'Whispers', icon: Feather },
        { id: 'moments', name: 'Moments', icon: Video },
        { id: 'quests', name: 'Quests', icon: Award },
        { id: 'nexus_souls', name: 'Nexus Souls', icon: Users },
    ], []);

    const currentUserProfile = useMemo(() => leaderboardData.find(entry => entry.id === user?.uid), [leaderboardData, user]);

    useEffect(() => {
        if (!db) {
            setLoading(false);
            return;
        }

        const fetchLeaderboards = async () => {
            setLoading(true);
            try {
                let fetchedLeaderboard = [];
                let collectionPath = 'artifacts/whispers-of-harmony/public/data/user_profiles';
                let orderByField = 'reputationScore';

                const timeFilter = (() => {
                    const now = new Date();
                    if (timePeriod === 'daily') return where('lastActivity', '>=', new Date(now.getFullYear(), now.getMonth(), now.getDate()));
                    if (timePeriod === 'weekly') return where('lastActivity', '>=', new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay()));
                    if (timePeriod === 'monthly') return where('lastActivity', '>=', new Date(now.getFullYear(), now.getMonth(), 1));
                    return null;
                })();

                if (view === 'nexus_souls') {
                    const weavesSnapshot = await getDocs(query(collection(db, 'artifacts/whispers-of-harmony/public/data/dreamWeaves'), orderBy('souls', 'desc')));
                    const nexusSouls = weavesSnapshot.docs.reduce((acc, doc) => {
                        const weave = doc.data();
                        if (weave.nexusId) {
                            acc[weave.nexusId] = (acc[weave.nexusId] || { souls: 0, name: weave.nexusName || 'Unknown Nexus' });
                            acc[weave.nexusId].souls += weave.souls;
                        }
                        return acc;
                    }, {});

                    fetchedLeaderboard = Object.entries(nexusSouls)
                        .map(([nexusId, data]) => ({ id: nexusId, name: data.name, value: data.souls }))
                        .sort((a, b) => b.value - a.value);
                } else {
                    orderByField = {
                        reputation: 'reputationScore',
                        earnings: 'totalEarnings',
                        whispers: 'whispersCount',
                        moments: 'momentsCount',
                        quests: 'questsCompletedCount',
                    }[view] || 'reputationScore';
                    
                    const q = query(collection(db, collectionPath), orderBy(orderByField, 'desc'), limit(100), ...(timeFilter ? [timeFilter] : []));
                    const snapshot = await getDocs(q);
                    fetchedLeaderboard = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), value: doc.data()[orderByField] }));
                }
                
                setPrevLeaderboardData(leaderboardData);
                setLeaderboardData(fetchedLeaderboard);

            } catch (err) {
                setError(err);
                console.error("Error fetching leaderboard:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaderboards();
    }, [db, view, timePeriod]);

    const renderLeaderboardEntries = () => {
        const filteredData = leaderboardData.filter(entry =>
            (entry.displayName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (entry.name?.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        if (filteredData.length === 0) {
            return (
                <div className="text-center text-gray-400 italic py-8">
                    <SearchX size={48} className="mx-auto mb-4" />
                    <p>No matching leaders found.</p>
                </div>
            );
        }

        return (
            <div className="max-w-3xl mx-auto space-y-3 custom-scrollbar overflow-y-auto pr-2">
                <AnimatePresence>
                    {filteredData.map((entry, index) => {
                        const isUserEntry = user && entry.id === user.uid;
                        const isUserNexus = view === 'nexus_souls' && user?.nexusId === entry.id;
                        const prevRank = prevLeaderboardData.findIndex(p => p.id === entry.id);
                        const rankChange = prevRank === -1 ? 'none' : prevRank > index ? 'ascend' : prevRank < index ? 'descend' : 'none';
                        const isSelected = compareMode && selectedForComparison.some(s => s.id === entry.id);
                        const rankIconColor = index === 0 ? 'text-yellow-400' : index === 1 ? 'text-gray-400' : index === 2 ? 'text-orange-400' : 'text-gray-500';
                        const displayValue = entry.value?.toLocaleString() ?? 0;
                        const displayLabel = categories.find(c => c.id === view)?.name ?? '';

                        return (
                            <motion.div
                                key={entry.id}
                                layout
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                                onClick={() => compareMode && handleSelectForComparison(entry)}
                                className={`bg-gray-800/50 border border-gray-700 rounded-lg p-4 flex items-center justify-between transition-all duration-300 hover:scale-[1.01] hover:shadow-lg hover:border-indigo-400 hover:shadow-indigo-500/30 ${isUserEntry || isUserNexus ? 'my-rank-sparkle' : ''} ${compareMode ? 'cursor-pointer' : ''} ${isSelected ? 'ring-2 ring-indigo-500' : ''}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`text-2xl font-bold w-8 text-center ${rankIconColor}`}>
                                        {index < 3 ? <Crown size={32} className="mx-auto" fill={['#FFD700', '#C0C0C0', '#CD7F32'][index]} stroke={['#FFD700', '#C0C0C0', '#CD7F32'][index]} /> : `#${index + 1}`}
                                    </div>
                                    {rankChange !== 'none' && (
                                        <span className={`text-sm ${rankChange === 'ascend' ? 'text-green-400' : 'text-red-400'}`}>
                                            {rankChange === 'ascend' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                                        </span>
                                    )}
                                    <img
                                        src={entry.photoURL || entry.emblemUrl || "https://placehold.co/40x40/AEC6CF/FFFFFF?text=W"}
                                        alt={entry.displayName || entry.name}
                                        className={`w-10 h-10 rounded-full object-cover ${index < 3 ? `avatar-aura-${['gold', 'silver', 'bronze'][index]}`: ''}`}
                                    />
                                    <div>
                                        <h2 className="text-lg font-bold text-white">{entry.displayName || entry.name}</h2>
                                        <p className="text-sm text-gray-400">{entry.title || ''}</p>
                                        {view !== 'nexus_souls' && entry.vibeScore && <VibeOrb vibeScore={entry.vibeScore} />}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xl font-bold text-cyan-300">{displayValue}</p>
                                    <p className="text-xs text-gray-500">{displayLabel}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
        );
    };

    return (
        <div className={`w-full h-full bg-gradient-to-b from-slate-900/90 to-black/95 backdrop-blur-xl text-white p-4 md:p-8 flex flex-col overflow-hidden transition-all duration-500 ${view === 'reputation' ? 'bg-player-aura' : view === 'nexus_souls' ? 'bg-nexus-aura' : ''}`}>
            <div className="text-center mb-6 flex-shrink-0">
                <h1 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 font-orbitron drop-shadow-sm flex items-center justify-center gap-3">
                    {categories.find(c => c.id === view)?.icon && React.createElement(categories.find(c => c.id === view).icon, { size: 32, className: "text-purple-400"})}
                    Pantheon of Harmony {loading && <Loader2 size={24} className="animate-spin text-indigo-400" />}
                </h1>
                <p className="text-gray-300 mt-2 font-light">See who reigns supreme in the cosmos.</p>
                {user && currentUserProfile && (
                    <div className="mt-4 p-4 bg-gray-800/40 backdrop-blur-md border border-indigo-500/30 rounded-xl max-w-md mx-auto shadow-lg">
                        <h3 className="text-lg font-bold text-indigo-300 mb-2 font-orbitron">Your Celestial Progress</h3>
                        <p className="text-sm text-white">Current {categories.find(cat => cat.id === view)?.name}: <span className="font-bold text-cyan-300">{currentUserProfile.value?.toLocaleString() || 0}</span></p>
                        {getNextMilestone(currentUserProfile.value || 0, view) ? (
                            <p className="text-sm text-gray-400">Next Milestone: <span className="font-bold text-cyan-300">{getNextMilestone(currentUserProfile.value || 0, view)}</span></p>
                        ) : (
                            <p className="text-sm text-gray-400">You've reached the highest visible milestone!</p>
                        )}
                    </div>
                )}
                <div className="mt-4 flex justify-center space-x-2 overflow-x-auto custom-scrollbar p-1">
                    {categories.map(category => (
                        <button
                            key={category.id}
                            onClick={() => setView(category.id)}
                            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all duration-200 border border-white/10 ${view === category.id ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' : 'bg-gray-800/60 text-gray-400 hover:bg-gray-700/80 hover:text-white'}`}
                        >
                            <category.icon size={16} /> {category.name}
                        </button>
                    ))}
                </div>
                <div className="mt-4 flex justify-center space-x-2">
                    {['daily', 'weekly', 'monthly', 'all_time'].map(period => (
                        <button key={period} onClick={() => setTimePeriod(period)} className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${timePeriod === period ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-800/60 text-gray-400 hover:bg-gray-700/80'}`}>{period.charAt(0).toUpperCase() + period.slice(1)}</button>
                    ))}
                </div>
                <div className="mt-4 relative max-w-md mx-auto w-full">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-gray-800/60 backdrop-blur-sm border border-gray-600/50 rounded-full py-2 pl-10 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                    />
                </div>
                <div className="mt-4 flex justify-center gap-4">
                    <button onClick={() => setCompareMode(!compareMode)} className={`px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition-all duration-200 shadow-lg ${compareMode ? 'bg-red-600/80 hover:bg-red-600 text-white' : 'bg-blue-600/80 hover:bg-blue-600 text-white'}`}>
                        {compareMode ? <X size={16} /> : <Scale size={16} />}
                        {compareMode ? 'Cancel Compare' : 'Compare Leaders'}
                    </button>
                    {user && (
                        <button onClick={handleShareRank} className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 bg-purple-600/80 hover:bg-purple-600 text-white transition-all duration-200 shadow-lg">
                            <Share2 size={16} /> Share My Rank
                        </button>
                    )}
                </div>
            </div>
            <div className="flex-grow overflow-y-auto custom-scrollbar pb-20 relative">
                {loading ? <LeaderboardSkeleton /> :
                 error ? <p className="text-red-500 p-4 text-center">Error loading leaderboard: {error.message}</p> :
                 renderLeaderboardEntries()}
            </div>

            {compareMode && selectedForComparison.length === 2 && (
                <CompareStatsModal
                    entry1={selectedForComparison[0]}
                    entry2={selectedForComparison[1]}
                    onClose={() => { setCompareMode(false); setSelectedForComparison([]); }}
                    viewType={view}
                />
            )}
        </div>
    );
};

export default LeaderboardPage;